// Client-side elevation sampling from free public terrarium DEM tiles (AWS
// elevation-tiles-prod, terrarium RGB encoding). Resolution is ~30 m at the
// sampled zoom, so the returned gain/loss is an approximation, not survey
// grade. A small noise floor with hysteresis keeps DEM quantization steps from
// inflating the total.

const TILE_ZOOM = 13;
const NOISE_FLOOR_M = 3;
const tileUrl = (z: number, x: number, y: number) =>
  `https://s3.amazonaws.com/elevation-tiles-prod/terrarium/${z}/${x}/${y}.png`;

// Decoded tiles, keyed "z/x/y". Stores the in-flight promise so concurrent
// samples of the same tile share one fetch/decode. null = fetch or canvas
// readback failed (e.g. missing CORS headers taint the canvas).
const tileCache = new Map<string, Promise<ImageData | null>>();

function lngLatToTile(lng: number, lat: number, z: number) {
  const n = 2 ** z;
  const x = ((lng + 180) / 360) * n;
  const latRad = (lat * Math.PI) / 180;
  const y =
    ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n;
  return { x, y };
}

function loadTile(z: number, x: number, y: number): Promise<ImageData | null> {
  const key = `${z}/${x}/${y}`;
  let p = tileCache.get(key);
  if (!p) {
    p = new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          if (!ctx) return resolve(null);
          ctx.drawImage(img, 0, 0);
          resolve(ctx.getImageData(0, 0, img.width, img.height));
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = tileUrl(z, x, y);
    });
    tileCache.set(key, p);
  }
  return p;
}

async function elevationAt(lng: number, lat: number): Promise<number | null> {
  const { x, y } = lngLatToTile(lng, lat, TILE_ZOOM);
  const tx = Math.floor(x);
  const ty = Math.floor(y);
  const img = await loadTile(TILE_ZOOM, tx, ty);
  if (!img) return null;
  const px = Math.min(img.width - 1, Math.floor((x - tx) * img.width));
  const py = Math.min(img.height - 1, Math.floor((y - ty) * img.height));
  const i = (py * img.width + px) * 4;
  const r = img.data[i];
  const g = img.data[i + 1];
  const b = img.data[i + 2];
  return r * 256 + g + b / 256 - 32768;
}

// Total ascent/descent (meters) along a [lng, lat] route. Returns zeros when no
// elevation could be sampled (offline, or the tile host blocks canvas readback).
export async function routeElevation(
  coords: [number, number][],
): Promise<{ gain: number; loss: number }> {
  const elevs: number[] = [];
  for (const [lng, lat] of coords) {
    const e = await elevationAt(lng, lat);
    if (e != null) elevs.push(e);
  }
  let gain = 0;
  let loss = 0;
  if (elevs.length >= 2) {
    // Hysteresis: accumulate only once the change since the last committed
    // point clears the noise floor, so jitter under NOISE_FLOOR_M is ignored
    // while sustained climbs still count.
    let ref = elevs[0];
    for (let i = 1; i < elevs.length; i++) {
      const d = elevs[i] - ref;
      if (d > NOISE_FLOOR_M) {
        gain += d;
        ref = elevs[i];
      } else if (d < -NOISE_FLOOR_M) {
        loss += -d;
        ref = elevs[i];
      }
    }
  }
  return { gain, loss };
}
